import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import moment from 'moment/moment';
import style from './styles.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextAreaDropdownController } from '@components-clean/core/text-area-dropdown';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { GroupedByMultiSelectOptionStringId } from '@interfaces-clean/dropdowns';
import {
    SubjectObservationGridRow,
    SubjectObservationType,
} from '@interfaces-clean/subject-monitoring/subject-observations';
import { sortStudyMemberData } from '@pages-clean/subject-monitoring/components/dialogs/helpers';
import { assumeUtc } from '@utils-clean/date-time-helpers';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { SubjectMonitoringStudyMemberApi } from '@apis/bff-api/subject-monitoring/subject-monitoring-study-member-api';
import SubjectObservationsApi from '@apis/bff-api/subject-monitoring/subject-observations';

interface EditSmObservationDialogProps {
    studyRef: string;
    observationToEdit: SubjectObservationGridRow;
    onClose: () => void;
    onSubmit: (id: number) => void;
    /** When true, the observation type is changed to Manual on save (used when converting a Note) */
    convertToManual?: boolean;
}

interface EditSmObservationForm {
    owner: GroupedByMultiSelectOptionStringId | null;
    closedDate: Date | null;
    observationDate: Date | null;
    observation: string;
    rootCause: string | null;
    comments: string | null;
}

const COMPONENT_NAME = 'edit-sm-observation-dialog';

const EditSmObservationDialog = ({
    studyRef,
    observationToEdit,
    onSubmit,
    onClose,
    convertToManual,
}: EditSmObservationDialogProps) => {
    const { t } = useTranslation('sm');
    const { t: mtkTranslation }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit',
    });

    const dialogTitle = `${mtkTranslation('editObservationDialog.title')} ${observationToEdit.record_id}`;
    const createdBy = `${moment(observationToEdit?.created_date).format('DD-MMM-YYYY')} by ${observationToEdit?.created_by}`;
    const updatedBy = `${moment(observationToEdit?.updated_date).format('DD-MMM-YYYY')} by ${observationToEdit?.updated_by}`;

    const { data: smStudyMembersData, isLoading: isLoadingStudyMembers } =
        SubjectMonitoringStudyMemberApi.useGetAllStudyMembers(studyRef, true);

    const { trigger: updateObservation, isMutating: isUpdatingObservation } =
        SubjectObservationsApi.useUpdate(studyRef);

    const defaultValues = {
        owner: null,
        observation: observationToEdit?.observation ?? '',
        closedDate: observationToEdit?.closed_date ? new Date(observationToEdit.closed_date) : null,
        rootCause: observationToEdit?.root_cause ?? '',
        comments: observationToEdit?.comments ?? '',
        observationDate: observationToEdit.observation_date ? new Date(observationToEdit.observation_date) : null,
    };

    const {
        control,
        formState: { isValid, isDirty, isSubmitting },
        reset,
        handleSubmit,
    } = useForm<EditSmObservationForm>({ mode: 'all', defaultValues });

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const owners: GroupedByMultiSelectOptionStringId[] = useMemo(() => {
        const sortedOwners = sortStudyMemberData(smStudyMembersData);

        if (observationToEdit.owner_ref) {
            const currentOwner = sortedOwners.find((x) => x.id === observationToEdit.owner_ref);
            if (currentOwner) {
                reset((currentDefaults) => {
                    return { ...currentDefaults, owner: currentOwner };
                });
            }
        }

        return sortedOwners;
    }, [observationToEdit.owner_ref, reset, smStudyMembersData]);

    const dataIsLoading = isLoadingStudyMembers || isUpdatingObservation;

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    const onUpdateObservation = async (formData: EditSmObservationForm) => {
        try {
            const { observation, closedDate, comments, owner, rootCause, observationDate } = formData;

            const response = await updateObservation({
                observation,
                rootCause,
                comments,
                ownerRef: owner?.id,
                observationId: parseInt(observationToEdit!.id, 10),
                closedDate: closedDate ? assumeUtc(closedDate) : null,
                observationDate: observationDate ? assumeUtc(observationDate) : undefined,
                observationType: convertToManual ? SubjectObservationType.Manual : undefined,
            });

            if (!response || !response.success) {
                return 0;
            }

            return response.id!;
        } catch {
            return 0;
        }
    };

    const onSubmitHandler = async (formData: EditSmObservationForm) => {
        const id = await onUpdateObservation(formData);
        onSubmit(id);
    };

    return (
        <DraggableDialog
            dataTestId="edit-sm-observation-dialog"
            className={cn(style.kWindowContent)}
            title={dialogTitle}
            createdUpdatedInfo={<CreatedUpdatedInfo created={createdBy} updated={updatedBy} />}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmitHandler)()}
            primaryBtnLabel={t('common:update')}
            initialHeight={665}
            initialWidth={600}
            bodyPadding="none"
            createdUpdatedBackgroundColour="gray"
            primaryBtnDisabled={!isValid || (!convertToManual && !isDirty) || isSubmitting || dataIsLoading}
            secondaryBtnDisabled={isSubmitting}
        >
            <div className="k-w-full k-h-full k-d-flex-col">
                <div>
                    <div className="k-py-sm">
                        <div className={cn(style.divider, 'k-shrink-0 k-d-flex')}>
                            <div className="k-flex k-justify-content-center k-flex-row k-d-flex k-align-items-center k-gap-2">
                                <div>{mtkTranslation('editObservationDialog.numOfReviews')}</div>
                                <div className={cn(style.editSiteObservationTopInfoDetailLabel)}>
                                    {observationToEdit.reviews}
                                </div>
                            </div>
                            <div className="k-flex k-justify-content-center k-flex-row k-d-flex k-align-items-center k-gap-2">
                                <div>{mtkTranslation('editObservationDialog.numOfOpenActions')}</div>
                                <div className={cn(style.editSiteObservationTopInfoDetailLabel)}>
                                    {observationToEdit.open_actions}
                                </div>
                            </div>
                            <div className="k-flex k-justify-content-center k-flex-row k-d-flex k-align-items-center k-gap-2">
                                <div>{mtkTranslation('editObservationDialog.recentAction')}</div>
                                <div className={cn(style.editSiteObservationTopInfoDetailLabel)}>
                                    {stringDateWithoutTimeFromUtc(observationToEdit.recent_action, 'DD-MMM-YYYY') ??
                                        'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={cn(style.editObservationInfo, 'k-pt-md k-px-md')}>
                    <div className={cn(style.divider, 'k-shrink-0 k-d-flex')}>
                        <div className="k-flex k-justify-content-left k-flex-row k-d-flex !k-basis-1/2 ">
                            <div className={cn(style.editObservationInfoLabelLong, 'k-font-medium')}>
                                {mtkTranslation('editObservationDialog.subject')}
                            </div>
                            <div className={cn(style.editSiteObservationTopInfoDetailLabel)}>
                                {observationToEdit.subject}
                            </div>
                        </div>
                        <div className="k-flex k-justify-content-left k-flex-row k-d-flex !k-basis-1/2">
                            <div className="k-font-medium k-pr-md">
                                {mtkTranslation('editObservationDialog.status')}
                            </div>
                            <div className={cn(style.editSiteObservationTopInfoDetailLabel)}>
                                {observationToEdit.status}
                            </div>
                        </div>
                    </div>
                    <div className={cn(style.divider, 'k-shrink-0 k-d-flex')}>
                        <div className="k-flex k-justify-content-left k-flex-row k-d-flex !k-basis-1/2">
                            <div className={cn(style.editObservationInfoLabelLong, 'k-font-medium')}>
                                {mtkTranslation('editObservationDialog.associationLabel')}
                            </div>
                            <div className={cn(style.editSiteObservationTopInfoDetailLabel, 'k-d-flex')}>
                                {observationToEdit.association}
                            </div>
                        </div>
                        <div className="k-flex k-justify-content-left k-flex-row k-d-flex !k-basis-1/2">
                            <div className="k-font-medium k-pr-md">
                                {mtkTranslation('editObservationDialog.datasetOpenedOn')}
                            </div>
                            <div className={cn(style.editSiteObservationTopInfoDetailLabel)}>
                                {stringDateWithoutTimeFromUtc(observationToEdit.dataset_opened_on, 'DD-MMM-YYYY')}
                            </div>
                        </div>
                    </div>
                    {observationToEdit.panel && (
                        <div className="k-shrink-0 k-d-flex k-justify-content-left k-align-items-center">
                            <div className={cn(style.editObservationInfoLabelLong, 'k-font-medium')}>
                                {mtkTranslation('editObservationDialog.panel')}
                            </div>
                            <div className={cn(style.editSiteObservationTopInfoDetailLabel, 'k-d-flex')}>
                                {observationToEdit.panel}
                                {observationToEdit?.panel_active === 'No' && (
                                    <div className={cn(style.inactiveIcon, 'k-pl-sm')}>
                                        <InactiveIcon
                                            toolTipText={t('common:inactiveIcon')}
                                            dataTestId="inactive-icon"
                                            tooltipPosition="bottom"
                                            className="k-mr-2"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {observationToEdit.data_listing && (
                        <div className="k-shrink-0 k-d-flex k-justify-content-left k-align-items-center">
                            <div className={cn(style.editObservationInfoLabelLong, 'k-font-medium')}>
                                {mtkTranslation('editObservationDialog.dataListing')}
                            </div>
                            <div className={cn(style.editSiteObservationTopInfoDetailLabel, 'k-d-flex')}>
                                {observationToEdit.data_listing}
                                {observationToEdit?.data_listing_active === 'No' && (
                                    <div className={cn(style.inactiveIcon, 'k-pl-sm')}>
                                        <InactiveIcon
                                            toolTipText={t('common:inactiveIcon')}
                                            dataTestId="inactive-icon"
                                            tooltipPosition="bottom"
                                            className="k-mr-2"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="k-shrink-0 k-d-flex k-justify-content-left k-align-items-center ">
                        <div className={cn(style.editObservationInfoLabelLong, 'k-font-medium')}>
                            {mtkTranslation('editObservationDialog.decision')}
                        </div>
                        <div className={cn(style.editSiteObservationTopInfoDetailLabel, 'k-d-flex')}>
                            {observationToEdit.decision ?? 'N/A'}
                            {observationToEdit?.decision_active === 'No' && (
                                <div className={cn(style.inactiveIcon, 'k-pl-sm')}>
                                    <InactiveIcon
                                        toolTipText={t('common:inactiveIcon')}
                                        dataTestId="inactive-icon"
                                        tooltipPosition="bottom"
                                        className="k-mr-2"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="k-shrink-0 k-d-flex k-justify-content-left k-align-items-center">
                        <div className={cn(style.editObservationInfoLabelLong, 'k-font-medium')}>
                            {mtkTranslation('editObservationDialog.review')}
                        </div>
                        <div className={cn(style.editSiteObservationTopInfoDetailLabel, 'k-d-flex')}>
                            {observationToEdit.review_title ?? 'N/A'}
                        </div>
                    </div>
                </div>
                <form id="edit-site-observation-form" className="k-mt-md k-w-full k-pl-3 k-pr-3">
                    <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', style.formFieldGap)}>
                        <BasicDropDownController
                            control={control}
                            name="owner"
                            data-testid="new-review-lovs"
                            data={owners}
                            textField="value"
                            dataItemKey="id"
                            label={`${mtkTranslation('addObservationDialog.owner')}`}
                            readOnly={isSubmitting || isLoadingStudyMembers}
                            groupField="groupedBy"
                            allowClear
                        />
                    </div>

                    <div className="k-shrink-0 k-d-flex k-justify-content-start k-pb-md k-gap-4">
                        <div className="!k-basis-1/2">
                            <DatePickerController
                                control={control}
                                name="observationDate"
                                data-testid="datepicker-observationDate"
                                label={`${mtkTranslation('addObservationDialog.observationDate')} *`}
                                fillMode="solid"
                                required
                                readonly={isSubmitting}
                            />
                        </div>
                        <div className="k-flex-row !k-basis-1/2">
                            <DatePickerController
                                control={control}
                                name="closedDate"
                                data-testid="datepicker-closedDate"
                                label={mtkTranslation('addObservationDialog.closedDate')}
                                fillMode="solid"
                                readonly={isSubmitting}
                            />
                        </div>
                    </div>
                    <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', style.formFieldGap)}>
                        <TextAreaController
                            className={style.textAreaOverrides}
                            maxLength={3000}
                            control={control}
                            name="observation"
                            required
                            data-testid="field-select-observation"
                            label={`${mtkTranslation('addObservationDialog.observation')} *`}
                            readOnly={isSubmitting}
                        />
                    </div>
                    <div className="k-pb-md k-input-md">
                        <TextAreaDropdownController
                            maxLength={3000}
                            className={style.textAreaOverrides}
                            control={control}
                            name="rootCause"
                            data-testid="field-select-rootCause"
                            label={mtkTranslation('addObservationDialog.rootCause')}
                            rows={4}
                            fillMode="outline"
                            readOnly={isSubmitting}
                            expanded={observationToEdit.root_cause !== null && observationToEdit.root_cause !== ''}
                        />
                    </div>
                    <div className="k-pb-md k-input-md">
                        <TextAreaDropdownController
                            control={control}
                            className={style.textAreaOverrides}
                            name="comments"
                            data-testid="field-select-comments"
                            label={mtkTranslation('addObservationDialog.comments')}
                            rows={4}
                            fillMode="outline"
                            maxLength={3000}
                            readOnly={isSubmitting}
                            expanded={observationToEdit.comments !== null && observationToEdit.comments !== ''}
                        />
                    </div>
                </form>
            </div>
        </DraggableDialog>
    );
};

export default EditSmObservationDialog;
